import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import queryFn, { getDefault } from './query';

import { useIsMobileScreen } from '@/hooks/useMedia';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { CellLink } from '../DataTable';
import track, { useTracking } from 'react-tracking';
import { TabList, Tab } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';
import { Bones, Box, Heading } from '@simplywallst/ui-core';
import If from '@/components/If';
import { persistedQueryConfig } from '@/utilities/persistQueryClient/utilities';

import {
  Detail,
  Date,
  TickerSymbol,
  Name,
  TableRow,
  DataTable,
  NoData,
  SeeMore,
} from './styled';

export const QUERY_KEY_DASHBOARD_UPCOMING_EVENTS = [
  'DASHBOARD_UPCOMING_EVENTS',
] as const;

enum EventTypeId {
  'Earning Release' = 'earnings',
  'Ex-dividend' = 'dividends',
}

enum UpcomingEventsTab {
  EarningRelease = 'Earning Release',
  ExDividend = 'Ex-dividend',
}

const UpcomingEvents = () => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { trackEvent } = useTracking();
  const [viewCount, setViewCount] = useState(5);
  const [activeTab, setActiveTab] = useState<UpcomingEventsTab>(
    UpcomingEventsTab.EarningRelease
  );

  const handleTabSelect = useCallback(
    (event: TabOnClickEvent<UpcomingEventsTab>) => {
      const value = event.target.value;
      setActiveTab(value);
      trackEvent({
        type: 'track',
        action: 'click',
        modifier: `dashboard-upcoming-events-tab-${value}`,
      });
    },
    []
  );

  const handleSeeMore = useCallback(() => {
    setViewCount(10);
  }, []);
  const { data = getDefault(), status } = useQuery({
    queryKey: QUERY_KEY_DASHBOARD_UPCOMING_EVENTS,
    queryFn,
    ...persistedQueryConfig,
  });
  return (
    <Box as="section" marginBottom="x4">
      {!isMobileScreen && (
        <Box marginTop="x1" marginBottom="x2">
          <Heading as="h1" typography="xSmall" color="whiteRGBA" opacity={0.7}>
            Upcoming Events
          </Heading>
        </Box>
      )}
      <TabList templateColumns="repeat(2, max-content)">
        <Tab<UpcomingEventsTab>
          data-cy-id="dashboard-upcoming-events-tab-earnings"
          selected={activeTab === UpcomingEventsTab.EarningRelease}
          onClick={handleTabSelect}
          value={UpcomingEventsTab.EarningRelease}
          disabled={status === 'pending'}
        >
          {UpcomingEventsTab.EarningRelease}
        </Tab>
        <Tab<UpcomingEventsTab>
          data-cy-id="dashboard-upcoming-events-tab-dividends"
          selected={activeTab === UpcomingEventsTab.ExDividend}
          onClick={handleTabSelect}
          value={UpcomingEventsTab.ExDividend}
          disabled={status === 'pending'}
        >
          {UpcomingEventsTab.ExDividend}
        </Tab>
      </TabList>
      <DataTable
        columnTextAlignments="l"
        data-cy-id="dashboard-upcoming-events-table"
      >
        <tbody>
          <If
            match={
              status === 'success' &&
              typeof data[EventTypeId[activeTab]] !== 'undefined' &&
              data[EventTypeId[activeTab]].length === 0
            }
          >
            <TableRow>
              <td>
                <NoData>No upcoming events</NoData>
              </td>
            </TableRow>
          </If>
          {typeof data[EventTypeId[activeTab]] !== 'undefined' &&
            data[EventTypeId[activeTab]].slice(0, viewCount).map((n) => (
              <TableRow
                key={`upcoming-events-item-${n.uniqueSymbol}-${n.id}`}
                data-cy-id="dashboard-upcoming-events-item"
              >
                <Detail>
                  <CellLink
                    to={n.canonicalUrl}
                    data-cy-id="dashboard-upcoming-events-link"
                  >
                    <Date>{n.date}</Date>
                    <TickerSymbol>{n.tickerSymbol}</TickerSymbol>
                    <Name>{n.companyName}</Name>
                  </CellLink>
                </Detail>
              </TableRow>
            ))}
          <If match={status === 'pending'}>
            <TableRow>
              <td>
                <span>
                  <Bones />
                </span>
              </td>
            </TableRow>
          </If>
          <If
            match={
              typeof data[EventTypeId[activeTab]] !== 'undefined' &&
              data[EventTypeId[activeTab]].length > 5 &&
              viewCount !== 10
            }
          >
            <SeeMore>
              <td colSpan={3}>
                <button
                  data-cy-id="dashboard-portfolios-table-see-more"
                  onClick={() => {
                    handleSeeMore();
                    trackEvent({
                      action: 'click',
                      modifier: 'see_more',
                    });
                  }}
                >
                  See More
                </button>
              </td>
            </SeeMore>
          </If>
        </tbody>
      </DataTable>
    </Box>
  );
};

export default track({
  type: 'track',
  subject: 'upcoming_events_module',
})(UpcomingEvents);
